import { useState, useEffect, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import KpiCard from "@/components/KpiCard";
import ExpenseForm from "@/components/ExpenseForm";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Receipt, Wallet, Hourglass, HandCoins, Download, Loader2, Plus, Check, X, Trash2,
  Briefcase, Tag, Image as ImageIcon, Sparkles,
} from "lucide-react";
import { fileUrl } from "@/lib/api";

const STATUS = {
  pending: { l: "Pendiente", c: "bg-amber-100 text-amber-700" },
  approved: { l: "Aprobado", c: "bg-emerald-100 text-emerald-700" },
  rejected: { l: "Rechazado", c: "bg-rose-100 text-rose-700" },
};
const eur = (n) => `${Number(n || 0).toFixed(2)} €`;

export default function Expenses() {
  const [summary, setSummary] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [projects, setProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: "", project_id: "", category_id: "" });
  const [addOpen, setAddOpen] = useState(false);
  const [proj, setProj] = useState({ name: "", client: "", location: "" });
  const [catName, setCatName] = useState("");
  const [viewPhoto, setViewPhoto] = useState(null);

  const loadMeta = useCallback(async () => {
    const [p, c, e] = await Promise.all([api.get("/projects"), api.get("/expense-categories"), api.get("/employees")]);
    setProjects(p.data); setCategories(c.data); setEmployees(e.data);
  }, []);

  const loadData = useCallback(async () => {
    const qs = new URLSearchParams(Object.entries(filters).filter(([, v]) => v)).toString();
    const [s, ex] = await Promise.all([api.get("/expenses/summary"), api.get(`/expenses${qs ? `?${qs}` : ""}`)]);
    setSummary(s.data); setExpenses(ex.data); setLoading(false);
  }, [filters]);

  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => { loadData(); }, [loadData]);

  const review = async (id, status) => {
    try { await api.patch(`/expenses/${id}`, { status }); toast.success(status === "approved" ? "Gasto aprobado" : "Gasto rechazado"); loadData(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const removeExp = async (id) => {
    if (!window.confirm("¿Eliminar este gasto?")) return;
    await api.delete(`/expenses/${id}`); toast.success("Gasto eliminado"); loadData();
  };
  const exportCsv = async () => {
    try {
      const res = await api.get("/expenses/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "gastos.csv"; a.click(); URL.revokeObjectURL(url);
      toast.success("CSV descargado");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const addProject = async () => {
    if (!proj.name.trim()) return toast.error("Indica el nombre del trabajo");
    try { await api.post("/projects", proj); setProj({ name: "", client: "", location: "" }); toast.success("Proyecto creado"); loadMeta(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const closeProject = async (id, status) => { await api.patch(`/projects/${id}`, { status }); loadMeta(); };
  const removeProject = async (id) => { if (!window.confirm("¿Eliminar este proyecto?")) return; await api.delete(`/projects/${id}`); loadMeta(); loadData(); };
  const addCat = async () => {
    if (!catName.trim()) return;
    try { await api.post("/expense-categories", { name: catName.trim() }); setCatName(""); toast.success("Categoría creada"); loadMeta(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const removeCat = async (id) => { await api.delete(`/expense-categories/${id}`); loadMeta(); loadData(); };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Gastos</h1>
          <p className="text-slate-500 text-sm mt-1">Tickets con IA, repercusión a trabajos y exportación para contabilidad.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="border-slate-200" onClick={exportCsv} data-testid="export-expenses-btn"><Download className="h-4 w-4 mr-2" /> Exportar CSV</Button>
          <Dialog open={addOpen} onOpenChange={setAddOpen}>
            <DialogTrigger asChild><Button className="brand-btn" data-testid="add-expense-btn"><Plus className="h-4 w-4 mr-2" /> Nuevo gasto</Button></DialogTrigger>
            <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-heading">Registrar gasto</DialogTitle></DialogHeader>
              <ExpenseForm projects={projects} categories={categories} employees={employees} onSaved={() => { setAddOpen(false); loadData(); }} />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard testId="exp-total" label="Total gastos" value={eur(summary?.total)} icon={Wallet} accent />
        <KpiCard testId="exp-approved" label="Aprobados" value={eur(summary?.approved_total)} icon={Receipt} />
        <KpiCard testId="exp-pending" label="Pendientes" value={summary?.pending_count ?? 0} icon={Hourglass} />
        <KpiCard testId="exp-reimburse" label="A reembolsar" value={eur(summary?.to_reimburse)} icon={HandCoins} />
      </div>

      <Tabs defaultValue="list">
        <TabsList>
          <TabsTrigger value="list" data-testid="tab-exp-list">Gastos</TabsTrigger>
          <TabsTrigger value="projects" data-testid="tab-exp-projects">Trabajos / Proyectos</TabsTrigger>
          <TabsTrigger value="categories" data-testid="tab-exp-categories">Categorías</TabsTrigger>
          <TabsTrigger value="report" data-testid="tab-exp-report">Informe</TabsTrigger>
        </TabsList>

        {/* LISTA */}
        <TabsContent value="list" className="mt-6 space-y-4">
          <div className="flex flex-wrap gap-2">
            <Select value={filters.status || "all"} onValueChange={(v) => setFilters({ ...filters, status: v === "all" ? "" : v })}>
              <SelectTrigger className="w-40" data-testid="filter-status"><SelectValue placeholder="Estado" /></SelectTrigger>
              <SelectContent><SelectItem value="all">Todos los estados</SelectItem><SelectItem value="pending">Pendiente</SelectItem><SelectItem value="approved">Aprobado</SelectItem><SelectItem value="rejected">Rechazado</SelectItem></SelectContent>
            </Select>
            <Select value={filters.project_id || "all"} onValueChange={(v) => setFilters({ ...filters, project_id: v === "all" ? "" : v })}>
              <SelectTrigger className="w-52" data-testid="filter-project"><SelectValue placeholder="Proyecto" /></SelectTrigger>
              <SelectContent><SelectItem value="all">Todos los proyectos</SelectItem>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={filters.category_id || "all"} onValueChange={(v) => setFilters({ ...filters, category_id: v === "all" ? "" : v })}>
              <SelectTrigger className="w-44" data-testid="filter-category"><SelectValue placeholder="Categoría" /></SelectTrigger>
              <SelectContent><SelectItem value="all">Todas las categorías</SelectItem>{categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <Card className="border-slate-200 shadow-none overflow-hidden">
            {loading ? <div className="h-40 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div> : (
              <Table>
                <TableHeader><TableRow className="hover:bg-transparent">
                  <TableHead>Fecha</TableHead><TableHead>Empleado</TableHead><TableHead>Concepto</TableHead>
                  <TableHead>Proyecto</TableHead><TableHead>Pago</TableHead><TableHead className="text-right">Importe</TableHead>
                  <TableHead>Estado</TableHead><TableHead className="text-right">Acciones</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {expenses.map((x) => (
                    <TableRow key={x.id} data-testid={`expense-row-${x.id}`}>
                      <TableCell className="text-slate-600 whitespace-nowrap">{new Date(x.date + "T00:00:00").toLocaleDateString("es-ES", { day: "2-digit", month: "short" })}</TableCell>
                      <TableCell className="text-slate-700">{x.employee_name}</TableCell>
                      <TableCell>
                        <div className="font-medium text-slate-800 flex items-center gap-1.5">
                          {x.vendor || x.description || "—"}
                          {x.ai_used && <Sparkles className="h-3 w-3 text-sky-400" title="Leído con IA" />}
                          {x.photo_path && <button onClick={() => setViewPhoto(fileUrl(x.photo_path))} data-testid={`view-photo-${x.id}`}><ImageIcon className="h-3.5 w-3.5 text-slate-400 hover:text-sky-500" /></button>}
                        </div>
                        <div className="text-xs text-slate-400">{x.category_name || "Sin categoría"}</div>
                      </TableCell>
                      <TableCell>{x.project_name ? <Badge variant="secondary" className="text-xs gap-1"><Briefcase className="h-3 w-3" />{x.project_name}</Badge> : <span className="text-slate-300">—</span>}</TableCell>
                      <TableCell className="text-xs text-slate-500">{x.payment_label}</TableCell>
                      <TableCell className="text-right font-semibold tabular-nums">{eur(x.amount)}</TableCell>
                      <TableCell><Badge className={`${STATUS[x.status]?.c} border-0`}>{STATUS[x.status]?.l}</Badge></TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          {x.status === "pending" && <>
                            <Button size="icon" variant="ghost" className="text-emerald-600 hover:bg-emerald-50" onClick={() => review(x.id, "approved")} data-testid={`approve-${x.id}`}><Check className="h-4 w-4" /></Button>
                            <Button size="icon" variant="ghost" className="text-rose-600 hover:bg-rose-50" onClick={() => review(x.id, "rejected")} data-testid={`reject-${x.id}`}><X className="h-4 w-4" /></Button>
                          </>}
                          <Button size="icon" variant="ghost" className="hover:text-red-500" onClick={() => removeExp(x.id)} data-testid={`del-expense-${x.id}`}><Trash2 className="h-4 w-4" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!expenses.length && <TableRow><TableCell colSpan={8} className="text-center text-slate-400 py-10">No hay gastos con estos filtros.</TableCell></TableRow>}
                </TableBody>
              </Table>
            )}
          </Card>
        </TabsContent>

        {/* PROYECTOS */}
        <TabsContent value="projects" className="mt-6 space-y-4">
          <Card className="p-5 border-slate-200 shadow-none">
            <h3 className="font-heading font-semibold mb-3 flex items-center gap-2"><Briefcase className="h-4 w-4 text-slate-400" /> Nuevo trabajo fuera de oficina</h3>
            <div className="grid sm:grid-cols-4 gap-2">
              <Input placeholder="Nombre del trabajo *" value={proj.name} onChange={(e) => setProj({ ...proj, name: e.target.value })} data-testid="project-name" />
              <Input placeholder="Cliente" value={proj.client} onChange={(e) => setProj({ ...proj, client: e.target.value })} data-testid="project-client" />
              <Input placeholder="Ubicación" value={proj.location} onChange={(e) => setProj({ ...proj, location: e.target.value })} data-testid="project-location" />
              <Button onClick={addProject} className="brand-btn" data-testid="add-project-btn"><Plus className="h-4 w-4 mr-1" /> Crear</Button>
            </div>
          </Card>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {projects.map((p) => (
              <Card key={p.id} className="p-4 border-slate-200 shadow-none" data-testid={`project-${p.id}`}>
                <div className="flex items-start justify-between">
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-800 truncate">{p.name}</div>
                    <div className="text-xs text-slate-500">{p.client || "—"}{p.location ? ` · ${p.location}` : ""}</div>
                  </div>
                  <Badge variant={p.status === "closed" ? "outline" : "secondary"} className="shrink-0">{p.status === "closed" ? "Cerrado" : "Activo"}</Badge>
                </div>
                <div className="flex gap-2 mt-3">
                  <Button size="sm" variant="outline" className="flex-1" onClick={() => closeProject(p.id, p.status === "closed" ? "active" : "closed")} data-testid={`toggle-project-${p.id}`}>{p.status === "closed" ? "Reabrir" : "Cerrar"}</Button>
                  <Button size="icon" variant="ghost" className="hover:text-red-500" onClick={() => removeProject(p.id)} data-testid={`del-project-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </Card>
            ))}
            {!projects.length && <Card className="p-8 text-center text-slate-400 border-slate-200 shadow-none sm:col-span-2 lg:col-span-3">Aún no hay trabajos creados.</Card>}
          </div>
        </TabsContent>

        {/* CATEGORÍAS */}
        <TabsContent value="categories" className="mt-6">
          <Card className="p-5 border-slate-200 shadow-none max-w-xl">
            <h3 className="font-heading font-semibold mb-3 flex items-center gap-2"><Tag className="h-4 w-4 text-slate-400" /> Categorías de gasto</h3>
            <div className="flex gap-2 mb-4">
              <Input placeholder="Nueva categoría" value={catName} onChange={(e) => setCatName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addCat()} data-testid="category-name" />
              <Button onClick={addCat} variant="outline" data-testid="add-category-btn"><Plus className="h-4 w-4" /></Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {categories.map((c) => (
                <Badge key={c.id} variant="secondary" className="pl-3 pr-1 py-1.5 gap-1 text-sm" data-testid={`category-${c.id}`}>
                  {c.name}
                  <button onClick={() => removeCat(c.id)} className="ml-1 hover:text-red-500"><X className="h-3.5 w-3.5" /></button>
                </Badge>
              ))}
            </div>
          </Card>
        </TabsContent>

        {/* INFORME */}
        <TabsContent value="report" className="mt-6 grid lg:grid-cols-2 gap-6">
          <Card className="p-6 border-slate-200 shadow-none">
            <h3 className="font-heading text-lg font-semibold mb-4 flex items-center gap-2"><Briefcase className="h-4 w-4 text-slate-400" /> Coste por trabajo / proyecto</h3>
            <div className="space-y-2">
              {(summary?.by_project || []).map((p, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0 text-sm">
                  <span className="text-slate-700">{p.name}</span><span className="font-semibold tabular-nums">{eur(p.value)}</span>
                </div>
              ))}
              {!summary?.by_project?.length && <div className="text-center text-slate-400 py-6">Sin gastos repercutidos a proyectos.</div>}
            </div>
          </Card>
          <Card className="p-6 border-slate-200 shadow-none">
            <h3 className="font-heading text-lg font-semibold mb-4 flex items-center gap-2"><Tag className="h-4 w-4 text-slate-400" /> Gasto por categoría</h3>
            <div className="space-y-2">
              {(summary?.by_category || []).map((c, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0 text-sm">
                  <span className="text-slate-700">{c.name}</span><span className="font-semibold tabular-nums">{eur(c.value)}</span>
                </div>
              ))}
              {!summary?.by_category?.length && <div className="text-center text-slate-400 py-6">Sin datos.</div>}
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={!!viewPhoto} onOpenChange={(v) => !v && setViewPhoto(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Ticket</DialogTitle></DialogHeader>
          {viewPhoto && <img src={viewPhoto} alt="ticket" className="w-full rounded-lg border border-slate-200" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
